/**
 * "Can this unit take this stay?" — and, just as importantly, "do we actually know?"
 *
 * Every rule here was measured against the live Guesty portfolio (GUESTY_LIVE_VERIFICATION.md),
 * and two of them exist because reading the documentation produced the wrong answer:
 *
 *   · `minNights` is a MINIMUM. A unit with a 3-night minimum accepts a 90-night stay. An earlier
 *     version of this project claimed the opposite. Across the whole portfolio, the number of
 *     candidates rejected on minNights in six simulated MTR searches was zero.
 *   · `maxNights` is the field that can actually refuse a mid-term stay, and this project did not
 *     know it existed. Portfolio values run 90, 150, 180, 364, 365, 999, 1000, 1125 — so a unit at
 *     90 refuses a four-month lease. No unit is blocked by it today, and the filter is still real:
 *     a rule that happens to be satisfied is not the same as a rule that does not exist.
 *
 * VERDICTS
 *
 * Five, not two, and the split is the whole point. `unknown` is a first-class result — a unit we
 * cannot speak for is never quietly counted as unavailable, because "nothing is free" and "nobody
 * has looked" are opposite facts that produce opposite actions.
 */

import { datesBetween, addDays } from './mirror.js'

export const VERDICT = {
  FITS: 'fits',
  UNAVAILABLE: 'unavailable',
  /**
   * Blocked ONLY by Guesty's advance-booking window — nobody has opened these dates yet.
   *
   * This is not a supply fact and must never be shown as one. Measured across 16,040 real day
   * records on 2026-09-04: 6,309 of them — 39% of the calendar — carry the `bw` block, and 37 of
   * 40 units wall off at exactly 240 days out. A renter asking about anything more than eight
   * months ahead is currently told "nothing available", when the truth is "nobody has opened it".
   *
   * Mid-term rental lead times are long: corporate relocations, travel-nurse contracts and
   * academic terms are routinely booked eight to twelve months out. So this is a live commercial
   * constraint disguised as a data field, and separating it is the difference between "we have no
   * homes" and "change one setting".
   */
  NOT_YET_OPEN: 'not_yet_open',
  /**
   * Free, quotable, and conditional on the owner agreeing — the mid-term answer to a
   * booking-window block.
   *
   * DECIDED 2026-09-04 by the Product Owner, and it is the reason the window exists that settles
   * it. The 240-day wall is there to protect the SHORT-TERM business: nobody wants scattered
   * three-night bookings landing eight months out at prices set today and probably wrong by then.
   * That is a sound rule for short stays and this product does not touch it.
   *
   * It says nothing about mid-term. A nine-month-out enquiry for a year's lease is not an instant
   * booking; Snowball's actual process is to ask the owner, and the answer is usually yes. So a
   * booking-window block is not a constraint on a mid-term quote — it is a CONDITION on one.
   *
   * The effect is that 34 homes hidden behind a settings wall become quotable without changing a
   * setting that is doing useful work. The condition travels with the quote, so nothing is ever
   * promised that has not been confirmed.
   */
  NEEDS_OWNER_OK: 'needs_owner_ok',
  RESTRICTED: 'restricted',
  UNKNOWN: 'unknown',
  TOO_SHORT: 'too_short',
}

/** The Guesty block flag for the advance-booking window. */
const BOOKING_WINDOW_BLOCK = 'bw'

/**
 * 30+ nights is what makes a stay mid-term, and it is what makes the owner-confirmation rule safe.
 *
 * Below this length the booking window stays a hard block, because a short stay IS the thing the
 * window was put there to prevent. Softening it for every stay would quietly undo a deliberate
 * commercial decision in the name of a different business line.
 *
 * The number is the company's setting (`midTermMinimum.js`, 2026-10-07), re-exported here live.
 */
import { MID_TERM_MINIMUM_NIGHTS } from './midTermMinimum.js'
export { MID_TERM_MINIMUM_NIGHTS }

/**
 * True when a day is unavailable for one reason only: the booking window.
 *
 * A day can be both beyond the window AND genuinely let — 86 of the June 2027 sample were — and a
 * real booking always wins, because no setting change frees it.
 */
function onlyBookingWindow(day) {
  const blocks = day && typeof day.blocks === 'object' && day.blocks ? day.blocks : null
  if (!blocks) return false
  const trueFlags = Object.entries(blocks).filter(([, v]) => v === true).map(([k]) => k)
  return trueFlags.length > 0 && trueFlags.every((f) => f === BOOKING_WINDOW_BLOCK)
}

/**
 * A night somebody has — let, or out of sale — read off one fetched day.
 *
 * The one rule `verdictFor` uses to put a night in `booked`, written once so a caller that wants to
 * skip the obviously refused stays quickly (the Calendar, #460) cannot drift from it. No allotment
 * left is taken; a non-available night is taken unless the booking window is its only reason or it
 * still has allotment (the multi-unit shape, which `verdictFor` calls unknown instead).
 */
export function nightIsTaken(day) {
  if (!day) return false
  const allotment = typeof day.allotment === 'number' ? day.allotment : null
  if (allotment !== null && allotment <= 0) return true
  return day.status !== 'available' && allotment === null && !onlyBookingWindow(day)
}

/**
 * The nights a stay occupies: check-in through the night before check-out.
 * PRICING_RULES P-10 — "the checkout date is neither occupied nor billable".
 */
export const occupiedNights = (checkIn, checkOut) => {
  const all = datesBetween(checkIn, checkOut)
  return all.slice(0, -1)
}

/**
 * Read a boolean restriction EXPLICITLY.
 *
 * `ctd` was measured absent on 68 of 3,000 sampled days. Here absent and false imply the same
 * behaviour, and that coincidence is exactly what makes `if (day.ctd)` dangerous: the same
 * shortcut applied to `status` would be a defect, and the next person copies the shortcut, not
 * the reasoning.
 */
const isClosed = (value) => value === true

export function verdictFor(unit, mirror, { checkIn, checkOut, midTermMinimum = MID_TERM_MINIMUM_NIGHTS } = {}) {
  const nights = occupiedNights(checkIn, checkOut)
  if (nights.length === 0) throw new RangeError(`checkout must be after check-in: ${checkIn} → ${checkOut}`)

  // `ctd` applies to the departure DAY, which is not an occupied night — so the mirror must be
  // able to speak for one day beyond the last night, and a stay ending exactly at the window edge
  // is NOT fully known.
  const daysNeeded = [...nights, checkOut]

  const gaps = []
  const beyondEdge = []
  const booked = []
  const notYetOpen = []
  const restrictions = []
  /**
   * The departure day is a special case, and getting it wrong costs real inventory.
   *
   * Found by running this module against the live portfolio. A 120-night stay from 1 October has
   * all 120 of its NIGHTS inside a mirror ending 28 January — only the departure day, the 29th,
   * falls off the edge. Treating that as an unknown stay hid 34 genuinely bookable homes.
   *
   * That is the original bug wearing the opposite coat: instead of calling an unknown night
   * "unavailable", it calls a known stay "unknown". Both replace a fact with a guess.
   *
   * So the two are separated. Whether the NIGHTS are known decides the verdict. An unknown
   * departure day is a stated caveat on an otherwise good answer — and it is a small one:
   * `ctd` was measured true on none of 3,000 sampled days.
   */
  let departureUnknown = false

  for (const date of daysNeeded) {
    const isDeparture = date === checkOut
    const found = mirror.lookup(unit.id, date)
    if (found.outsideWindow) {
      if (isDeparture) departureUnknown = true
      else beyondEdge.push(date)
      continue
    }
    if (found.missing) {
      if (isDeparture) departureUnknown = true
      else gaps.push(date)
      continue
    }

    const day = found.day
    const isNight = date !== checkOut

    if (isNight) {
      const allotment = typeof day.allotment === 'number' ? day.allotment : null
      if (nightIsTaken(day)) {
        booked.push(date)
      } else if (day.status !== 'available') {
        // A non-available status with allotment remaining is the multi-unit shape. Every listing
        // in this portfolio is SINGLE, so we have never seen it — and guessing "available" here
        // is the one guess that can sell a home twice. Unknown, deliberately.
        if (allotment !== null && allotment > 0) gaps.push(date)
        else notYetOpen.push(date)
      }
    }

    if (date === checkIn && isClosed(day.cta)) {
      restrictions.push({ rule: 'cta', date, message: `Arrivals are closed on ${date}.` })
    }
    if (date === checkOut && isClosed(day.ctd)) {
      restrictions.push({ rule: 'ctd', date, message: `Departures are closed on ${date}.` })
    }
  }

  // Length restrictions are read from the ARRIVAL day, which is where Guesty applies them.
  const arrival = mirror.lookup(unit.id, checkIn)
  if (arrival.day) {
    const { minNights, maxNights } = arrival.day
    if (typeof maxNights === 'number' && nights.length > maxNights) {
      restrictions.push({
        rule: 'maxNights',
        date: checkIn,
        message: `This home accepts at most ${maxNights} nights; the stay is ${nights.length}.`,
      })
    }
    if (typeof minNights === 'number' && nights.length < minNights) {
      restrictions.push({
        rule: 'minNights',
        date: checkIn,
        message: `This home requires at least ${minNights} nights; the stay is ${nights.length}.`,
      })
    }
  }

  const base = {
    unit,
    nights: nights.length,
    checkIn,
    checkOut,
    booked,
    notYetOpen,
    restrictions,
    gaps,
    beyondEdge,
    departureUnknown,
    /** Rendered beside a positive answer, never instead of one. */
    caveat: departureUnknown
      ? `Every night is free. We cannot confirm the departure rules for ${checkOut} — our calendar ends on ${mirror.windowEnd}.`
      : null,
  }

  // ORDER MATTERS. Unknown outranks unavailable: a unit we cannot speak for must never be
  // reported as booked. Booked outranks restricted, because a home that is sold is sold whatever
  // its arrival rules say.
  if (beyondEdge.length > 0 || gaps.length > 0) {
    return {
      ...base,
      verdict: VERDICT.UNKNOWN,
      why: beyondEdge.length > 0
        ? `We hold no calendar data past ${mirror.windowEnd}.`
        : `${gaps.length} night${gaps.length > 1 ? 's are' : ' is'} missing from our copy of the calendar.`,
    }
  }
  if (booked.length > 0) {
    // A real booking outranks a booking-window block: no setting change frees a let home, so the
    // actionable answer is the one that cannot be acted on.
    return { ...base, verdict: VERDICT.UNAVAILABLE, why: bookedPhrase(booked) }
  }
  // A listing-level restriction is a hard no and outranks a conditional yes: maxNights refuses
  // the stay whether or not the owner would have agreed to it.
  if (restrictions.length > 0) {
    return { ...base, verdict: VERDICT.RESTRICTED, why: restrictions.map((r) => r.message).join(' ') }
  }
  if (notYetOpen.length > 0) {
    const isMidTerm = nights.length >= midTermMinimum
    return isMidTerm
      ? {
          ...base,
          verdict: VERDICT.NEEDS_OWNER_OK,
          why: `Free — no night is let. ${notYetOpen.length} of them fall past the calendar we have opened for short stays, so this one needs the owner's yes before it is promised.`,
        }
      : {
          ...base,
          verdict: VERDICT.NOT_YET_OPEN,
          why: `${notYetOpen.length} of these nights are past this home's booking window. Nothing is let, but a stay of ${nights.length} nights is not long enough to take outside it.`,
        }
  }
  return { ...base, verdict: VERDICT.FITS, why: `All ${nights.length} nights are free.` }
}

/** "booked 4–10 Oct" rather than a list of thirty dates nobody reads. */
function bookedPhrase(dates) {
  const sorted = [...dates].sort()
  const runs = []
  let start = sorted[0]
  let prev = sorted[0]
  for (const d of sorted.slice(1)) {
    if (d === addDays(prev, 1)) { prev = d; continue }
    runs.push([start, prev]); start = d; prev = d
  }
  runs.push([start, prev])
  const shown = runs.slice(0, 3).map(([a, b]) => (a === b ? a : `${a} to ${b}`))
  const more = runs.length > 3 ? `, and ${runs.length - 3} more` : ''
  return `Occupied ${shown.join(', ')}${more}.`
}

/**
 * Search the portfolio for one stay.
 *
 * Returns every group, including the ones a demo would hide. `unknown` is reported separately and
 * loudly: a search that found nothing because the window is too short is a different situation
 * from one that found nothing because everything is let, and the screen must be able to say which.
 */
export function searchAvailability({ units = [], mirror, checkIn, checkOut, minimumNights = MID_TERM_MINIMUM_NIGHTS } = {}) {
  const nights = occupiedNights(checkIn, checkOut).length
  const results = units.map((u) => verdictFor(u, mirror, { checkIn, checkOut, midTermMinimum: minimumNights }))

  const group = (v) => results.filter((r) => r.verdict === v)
  const fits = group(VERDICT.FITS)
  const unknown = group(VERDICT.UNKNOWN)

  return {
    checkIn,
    checkOut,
    nights,
    /** P-02's floor, surfaced here so the screen can refuse before it searches. */
    belowMidTermFloor: nights < minimumNights,
    window: { start: mirror.windowStart, end: mirror.windowEnd },
    /**
     * True when the stay runs past what the mirror holds. The screen MUST NOT report "no
     * availability" while this is set — it has to say we do not know that far out.
     */
    runsPastWindow: checkOut > mirror.windowEnd,
    fits,
    unavailable: group(VERDICT.UNAVAILABLE),
    /**
     * Reported separately from `unavailable` because it is the only group on this screen that a
     * person can do something about today, and because counting it as unavailable would report a
     * settings problem as a supply problem.
     */
    notYetOpen: group(VERDICT.NOT_YET_OPEN),
    /**
     * Quotable, conditional on the owner. Presented WITH the exact matches rather than below the
     * fold — they are free homes, and burying them recreates the problem this verdict exists to
     * solve — but always carrying their condition.
     */
    needsOwnerOk: group(VERDICT.NEEDS_OWNER_OK),
    restricted: group(VERDICT.RESTRICTED),
    unknown,
    tooShort: group(VERDICT.TOO_SHORT),
    counts: {
      searched: units.length,
      fits: fits.length,
      unknown: unknown.length,
      notYetOpen: group(VERDICT.NOT_YET_OPEN).length,
      needsOwnerOk: group(VERDICT.NEEDS_OWNER_OK).length,
      /** Everything a renter could actually be offered, conditional or not. */
      offerable: fits.length + group(VERDICT.NEEDS_OWNER_OK).length,
    },
  }
}

/**
 * Units that fit the same LENGTH of stay if the start moves.
 *
 * Measured on live data: shifting up to ±7 days adds only 4 to 6 further units against 35–57 exact
 * matches. It is a secondary list, not a co-equal one, and the screen should present it that way —
 * a renter who asked for the 1st does not want twelve options starting on the 6th.
 */
export function nearMisses({ units = [], mirror, checkIn, checkOut, shiftDays = 7 } = {}) {
  const nights = occupiedNights(checkIn, checkOut).length
  const exact = new Set(
    units.filter((u) => verdictFor(u, mirror, { checkIn, checkOut }).verdict === VERDICT.FITS).map((u) => u.id)
  )

  const out = []
  for (const u of units) {
    if (exact.has(u.id)) continue
    let best = null
    for (let shift = -shiftDays; shift <= shiftDays; shift++) {
      if (shift === 0) continue
      const from = addDays(checkIn, shift)
      const to = addDays(from, nights)
      const r = verdictFor(u, mirror, { checkIn: from, checkOut: to })
      if (r.verdict !== VERDICT.FITS) continue
      if (best === null || Math.abs(shift) < Math.abs(best.shift)) best = { shift, checkIn: from, checkOut: to }
    }
    if (best) out.push({ unit: u, ...best, nights })
  }
  // Closest to what the renter actually asked for, first.
  return out.sort((a, b) => Math.abs(a.shift) - Math.abs(b.shift))
}
